import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Plus,
    Trash2,
    Pencil,
    Loader2,
    Image as ImageIcon,
    Paperclip,
    Bold,
    Italic,
    Heading,
    List,
    Quote,
    Link as LinkIcon,
    Eye,
    X,
    Check,
    Upload,
    Pin,
} from 'lucide-react';
import { API, abs, formatDate } from '@/lib/mediaUrl';
import { humanSize } from '@/lib/format';
import { StoryCover } from '@/components/StoryCover';

const empty = {
    title: '',
    subtitle: '',
    body_md: '',
    cover_url: null,
    tags: [],
    attachments: [],
    status: 'draft',
    published_at: '',
    pinned: false,
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const StoriesManager = ({ passcode }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // post object or {} for new
    const [err, setErr] = useState('');

    const load = async () => {
        try {
            const r = await fetch(`${API}/admin/posts`, {
                headers: { 'X-Admin-Passcode': passcode },
            });
            if (r.ok) setPosts(await r.json());
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDelete = async (id) => {
        if (!window.confirm('Delete this story permanently?')) return;
        const r = await fetch(`${API}/admin/posts/${id}`, {
            method: 'DELETE',
            headers: { 'X-Admin-Passcode': passcode },
        });
        if (r.ok) setPosts((p) => p.filter((x) => x.id !== id));
        else setErr(`Delete failed (${r.status})`);
    };

    const openNew = () =>
        setEditing({ ...empty, published_at: todayISO() });

    const onPin = async (id) => {
        const r = await fetch(`${API}/admin/posts/${id}/pin`, {
            method: 'POST',
            headers: { 'X-Admin-Passcode': passcode },
        });
        if (r.ok) {
            const { pinned } = await r.json();
            setPosts((list) =>
                list.map((x) => (x.id === id ? { ...x, pinned } : x))
            );
        } else setErr(`Pin failed (${r.status})`);
    };

    const openEdit = (p) =>
        setEditing({
            ...empty,
            ...p,
            tags: p.tags || [],
            attachments: p.attachments || [],
            published_at: (p.published_at || '').slice(0, 10) || todayISO(),
        });

    const onSaved = (saved) => {
        setPosts((list) => {
            const others = list.filter((x) => x.id !== saved.id);
            return [saved, ...others];
        });
        setEditing(null);
    };

    return (
        <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                    <span>Stories · Blog CMS</span>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    data-testid="story-new-button"
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[12.5px] font-semibold hover:opacity-90 transition-opacity"
                >
                    <Plus size={14} /> New story
                </button>
            </div>

            {err && (
                <div className="mb-4 text-[12px] text-[var(--warn)] font-mono-brand uppercase tracking-[0.16em]">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="py-10 flex items-center justify-center text-[var(--ink-3)]">
                    <Loader2 size={20} className="animate-spin" />
                </div>
            ) : posts.length === 0 ? (
                <div className="py-8 text-center font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                    No stories yet — write your first
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {posts.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`story-row-${p.slug}`}
                            className="group relative flex flex-col rounded-xl border border-[var(--line-2)] bg-[var(--surface-2)] overflow-hidden"
                        >
                            <div className="relative aspect-[16/9] bg-[var(--surface)] overflow-hidden">
                                <StoryCover post={p} />
                                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                                    <span
                                        className={`inline-block px-2 py-0.5 rounded-full font-mono-brand text-[9px] uppercase tracking-[0.16em] backdrop-blur ${
                                            p.status === 'published'
                                                ? 'bg-[var(--accent)] text-[var(--accent-ink)]'
                                                : 'bg-black/50 border border-[var(--line-2)] text-[var(--ink-2)]'
                                        }`}
                                    >
                                        {p.status}
                                    </span>
                                    {p.pinned && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur font-mono-brand text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]">
                                            <Pin size={9} /> Pinned
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="p-3.5 flex flex-col gap-2 flex-1">
                                <div className="font-sans-brand text-[14px] text-[var(--ink)] leading-snug line-clamp-2">
                                    {p.title}
                                </div>
                                <div className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
                                    {formatDate(p.published_at) || '—'} · {p.read_minutes} min
                                </div>
                                <div className="mt-auto pt-2 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => onPin(p.id)}
                                        data-testid={`story-pin-${p.slug}`}
                                        data-cursor="hover"
                                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                                            p.pinned
                                                ? 'border-[var(--accent)] text-[var(--accent)]'
                                                : 'border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)]'
                                        }`}
                                        title={p.pinned ? 'Unpin' : 'Pin to top'}
                                    >
                                        <Pin size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => openEdit(p)}
                                        data-testid={`story-edit-${p.slug}`}
                                        data-cursor="hover"
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                                        title="Edit"
                                    >
                                        <Pencil size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(p.id)}
                                        data-testid={`story-delete-${p.slug}`}
                                        data-cursor="hover"
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--warn)] hover:border-[var(--warn)] transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {editing && (
                    <Editor
                        passcode={passcode}
                        initial={editing}
                        onClose={() => setEditing(null)}
                        onSaved={onSaved}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// ── Editor modal ──────────────────────────────────────────────
const Editor = ({ passcode, initial, onClose, onSaved }) => {
    const [f, setF] = useState(initial);
    const [tagStr, setTagStr] = useState((initial.tags || []).join(', '));
    const [preview, setPreview] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [uploading, setUploading] = useState('');
    const bodyRef = useRef(null);

    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    // Lock background page scroll while the editor modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    const upload = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(`${API}/admin/upload`, {
            method: 'POST',
            headers: { 'X-Admin-Passcode': passcode },
            body: fd,
        });
        if (!r.ok) throw new Error(`Upload failed (${r.status})`);
        const rec = await r.json();
        return {
            id: rec.id,
            url: `/api/files/${rec.id}`,
            filename: rec.original_filename,
            content_type: rec.content_type,
            size: rec.size,
        };
    };

    const onCover = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading('cover');
        try {
            const rec = await upload(file);
            set('cover_url', rec.url);
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setUploading('');
        }
    };

    const insertAtCursor = (text) => {
        const el = bodyRef.current;
        if (!el) {
            set('body_md', (f.body_md || '') + text);
            return;
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const val = f.body_md || '';
        const next = val.slice(0, start) + text + val.slice(end);
        set('body_md', next);
        requestAnimationFrame(() => {
            el.focus();
            el.selectionStart = el.selectionEnd = start + text.length;
        });
    };

    const wrapSelection = (before, after = before) => {
        const el = bodyRef.current;
        const val = f.body_md || '';
        if (!el) return insertAtCursor(before + after);
        const s = el.selectionStart;
        const e = el.selectionEnd;
        const sel = val.slice(s, e) || 'text';
        const next = val.slice(0, s) + before + sel + after + val.slice(e);
        set('body_md', next);
        requestAnimationFrame(() => {
            el.focus();
            el.selectionStart = s + before.length;
            el.selectionEnd = s + before.length + sel.length;
        });
    };

    const onInlineImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading('inline');
        try {
            const rec = await upload(file);
            insertAtCursor(`\n![${rec.filename}](${rec.url})\n`);
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setUploading('');
        }
    };

    const onAttachment = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading('attach');
        try {
            const recs = [];
            for (const file of files) recs.push(await upload(file));
            set('attachments', [...(f.attachments || []), ...recs]);
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setUploading('');
        }
    };

    const removeAttachment = (idx) =>
        set(
            'attachments',
            (f.attachments || []).filter((_, i) => i !== idx)
        );

    const save = async (status) => {
        if (!f.title.trim()) {
            setErr('Title is required');
            return;
        }
        setBusy(true);
        setErr('');
        const payload = {
            title: f.title,
            subtitle: f.subtitle || null,
            body_md: f.body_md || '',
            cover_url: f.cover_url || null,
            tags: tagStr.split(',').map((t) => t.trim()).filter(Boolean),
            attachments: f.attachments || [],
            status,
            published_at: f.published_at
                ? new Date(f.published_at).toISOString()
                : null,
            pinned: Boolean(f.pinned),
        };
        const isEdit = Boolean(f.id);
        try {
            const r = await fetch(
                `${API}/admin/posts${isEdit ? `/${f.id}` : ''}`,
                {
                    method: isEdit ? 'PUT' : 'POST',
                    headers: {
                        'X-Admin-Passcode': passcode,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                }
            );
            if (!r.ok) throw new Error(`Save failed (${r.status})`);
            onSaved(await r.json());
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setBusy(false);
        }
    };

    const Tool = ({ icon: Icon, onClick, label }) => (
        <button
            type="button"
            onClick={onClick}
            title={label}
            data-cursor="hover"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
        >
            <Icon size={14} />
        </button>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
            data-testid="story-editor"
            data-lenis-prevent
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                className="w-full max-w-[1000px] max-h-[94vh] flex flex-col overflow-hidden rounded-[22px] border border-[var(--line-2)] bg-[var(--bg)]"
            >
                <div className="shrink-0 flex items-center justify-between px-5 md:px-7 pt-5 pb-4 border-b border-[var(--line)]">
                    <div className="font-display text-2xl text-[var(--ink)]">
                        {f.id ? 'Edit story' : 'New story'}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="story-editor-close"
                        data-cursor="hover"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>

                {err && (
                    <div className="px-5 md:px-7 pt-3 text-[12px] text-[var(--warn)] font-mono-brand uppercase tracking-[0.16em]">
                        {err}
                    </div>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 md:px-7 py-5 flex flex-col gap-4" data-lenis-prevent>
                    <input
                        value={f.title}
                        onChange={(e) => set('title', e.target.value)}
                        placeholder="Story title"
                        data-testid="story-title-input"
                        className="w-full bg-transparent border border-[var(--line-2)] rounded-xl px-4 py-3 font-display text-2xl text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--accent)]"
                    />
                    <input
                        value={f.subtitle || ''}
                        onChange={(e) => set('subtitle', e.target.value)}
                        placeholder="Short subtitle (optional)"
                        data-testid="story-subtitle-input"
                        className="w-full bg-transparent border border-[var(--line-2)] rounded-xl px-4 py-2.5 font-sans-brand text-[14px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--accent)]"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="flex flex-col gap-1.5">
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                                Posting date
                            </span>
                            <input
                                type="date"
                                value={f.published_at || ''}
                                onChange={(e) => set('published_at', e.target.value)}
                                data-testid="story-date-input"
                                className="bg-transparent border border-[var(--line-2)] rounded-xl px-4 py-2.5 text-[14px] text-[var(--ink)] focus:outline-none focus:border-[var(--accent)]"
                            />
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                                Tags (comma separated)
                            </span>
                            <input
                                value={tagStr}
                                onChange={(e) => setTagStr(e.target.value)}
                                placeholder="design, ux, process"
                                data-testid="story-tags-input"
                                className="bg-transparent border border-[var(--line-2)] rounded-xl px-4 py-2.5 text-[14px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--accent)]"
                            />
                        </label>
                    </div>

                    {/* Cover */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <label
                            data-cursor="hover"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] text-[12.5px] text-[var(--ink)] hover:border-[var(--accent)] cursor-pointer transition-colors"
                        >
                            {uploading === 'cover' ? (
                                <Loader2 size={13} className="animate-spin" />
                            ) : (
                                <ImageIcon size={13} />
                            )}
                            {f.cover_url ? 'Change cover' : 'Add cover image'}
                            <input type="file" accept="image/*" hidden onChange={onCover} />
                        </label>
                        {f.cover_url && (
                            <div className="flex items-center gap-2">
                                <img
                                    src={abs(f.cover_url)}
                                    alt="cover"
                                    className="w-16 h-12 object-cover rounded-lg border border-[var(--line-2)]"
                                />
                                <button
                                    type="button"
                                    onClick={() => set('cover_url', null)}
                                    className="text-[var(--ink-3)] hover:text-[var(--warn)]"
                                    title="Remove cover"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Toolbar */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Tool icon={Bold} label="Bold" onClick={() => wrapSelection('**')} />
                        <Tool icon={Italic} label="Italic" onClick={() => wrapSelection('_')} />
                        <Tool
                            icon={Heading}
                            label="Heading"
                            onClick={() => insertAtCursor('\n## ')}
                        />
                        <Tool
                            icon={List}
                            label="List"
                            onClick={() => insertAtCursor('\n- ')}
                        />
                        <Tool
                            icon={Quote}
                            label="Quote"
                            onClick={() => insertAtCursor('\n> ')}
                        />
                        <Tool
                            icon={LinkIcon}
                            label="Link"
                            onClick={() => wrapSelection('[', '](https://)')}
                        />
                        <label
                            title="Insert image"
                            data-cursor="hover"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)] cursor-pointer transition-colors"
                        >
                            {uploading === 'inline' ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <ImageIcon size={14} />
                            )}
                            <input type="file" accept="image/*" hidden onChange={onInlineImage} />
                        </label>
                        <div className="flex-1" />
                        <button
                            type="button"
                            onClick={() => setPreview((p) => !p)}
                            data-testid="story-preview-toggle"
                            data-cursor="hover"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11.5px] font-medium transition-colors ${
                                preview
                                    ? 'border-[var(--accent)] text-[var(--accent)]'
                                    : 'border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)]'
                            }`}
                        >
                            <Eye size={12} /> {preview ? 'Editing' : 'Preview'}
                        </button>
                    </div>

                    {/* Body: editor + live preview */}
                    {preview ? (
                        <div
                            data-testid="story-preview"
                            className="story-prose min-h-[280px] border border-[var(--line-2)] rounded-xl px-4 py-4 font-sans-brand text-[15px] leading-[1.7] text-[var(--ink-2)]"
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    img: ({ src, alt }) => (
                                        <img
                                            src={abs(src)}
                                            alt={alt || ''}
                                            className="rounded-xl border border-[var(--line)] my-4 w-full"
                                        />
                                    ),
                                }}
                            >
                                {f.body_md || '_Nothing to preview yet._'}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <textarea
                            ref={bodyRef}
                            value={f.body_md}
                            onChange={(e) => set('body_md', e.target.value)}
                            placeholder="Write your story in Markdown…"
                            data-testid="story-body-input"
                            className="w-full min-h-[280px] bg-transparent border border-[var(--line-2)] rounded-xl px-4 py-3 font-mono-brand text-[13.5px] leading-[1.6] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--accent)] resize-y"
                        />
                    )}

                    {/* Attachments */}
                    <div>
                        <label
                            data-cursor="hover"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] text-[12.5px] text-[var(--ink)] hover:border-[var(--accent)] cursor-pointer transition-colors"
                        >
                            {uploading === 'attach' ? (
                                <Loader2 size={13} className="animate-spin" />
                            ) : (
                                <Paperclip size={13} />
                            )}
                            Add attachments
                            <input type="file" multiple hidden onChange={onAttachment} />
                        </label>
                        {(f.attachments || []).length > 0 && (
                            <div className="mt-3 flex flex-col gap-2">
                                {f.attachments.map((a, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between gap-3 rounded-lg border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2"
                                    >
                                        <span className="font-sans-brand text-[13px] text-[var(--ink)] truncate">
                                            {a.filename}
                                            {a.size ? (
                                                <span className="text-[var(--ink-3)]">
                                                    {' '}
                                                    · {humanSize(a.size)}
                                                </span>
                                            ) : null}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(i)}
                                            className="text-[var(--ink-3)] hover:text-[var(--warn)] shrink-0"
                                            title="Remove"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sticky footer — always visible actions */}
                <div className="shrink-0 flex items-center justify-between gap-3 px-5 md:px-7 py-4 border-t border-[var(--line)] flex-wrap">
                    <button
                        type="button"
                        onClick={() => set('pinned', !f.pinned)}
                        data-testid="story-pin-toggle"
                        data-cursor="hover"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-[12.5px] font-medium transition-colors ${
                            f.pinned
                                ? 'border-[var(--accent)] text-[var(--accent)]'
                                : 'border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)]'
                        }`}
                    >
                        <Pin size={13} /> {f.pinned ? 'Pinned to top' : 'Pin to top'}
                    </button>
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => save('draft')}
                            data-testid="story-save-draft"
                            data-cursor="hover"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--line-2)] text-[13px] font-medium text-[var(--ink)] hover:border-[var(--accent)] transition-colors disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                            Save draft
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => save('published')}
                            data-testid="story-publish"
                            data-cursor="hover"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                            Publish
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default StoriesManager;
